import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { billingApi } from '../../services/billing';
import type { SubscriptionPlan, BillingOverview } from '../../types';

function formatLimit(value: number, singular: string, plural: string): string {
  if (value >= 9999) return `Unlimited ${plural}`;
  return `Up to ${value} ${value === 1 ? singular : plural}`;
}

/** The middle plan (sort_order-wise) gets the "Most Popular" highlight */
function getMostPopularIndex(plans: SubscriptionPlan[]): number {
  if (plans.length < 2) return -1;
  // Pick the middle plan (index 1 in a 3-plan layout)
  return Math.floor(plans.length / 2);
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="h-5 w-5 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default function VendorBillingPlans() {
  const [redirectingPlanId, setRedirectingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => billingApi.getPlans().then((r) => r.data),
  });

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['billing', 'overview'],
    queryFn: () => billingApi.getOverview().then((r) => r.data),
  });

  const isLoading = plansLoading || overviewLoading;
  const sortedPlans = [...(plans ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const currentPlan = (overview as BillingOverview | undefined)?.current_plan ?? null;
  const currentSortOrder = currentPlan?.sort_order ?? -1;
  const mostPopularIdx = getMostPopularIndex(sortedPlans);

  const navigate = useNavigate();

  const handleChangePlan = async (plan: SubscriptionPlan) => {
    if (!plan.stripe_price_id_monthly) return;
    setRedirectingPlanId(plan.id);
    setError(null);
    try {
      const { data } = await billingApi.createCheckoutSession(
        plan.stripe_price_id_monthly,
        'monthly',
      );
      if (data.updated) {
        // Existing subscription was modified directly — go to success page
        navigate('/vendor/billing/success');
      } else if (data.checkout_url) {
        // New subscription — redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      }
    } catch {
      setError('Failed to change plan. Please try again.');
      setRedirectingPlanId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Subscription Plans"
        actions={
          <Link
            to="/vendor/billing"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Billing
          </Link>
        }
      />

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : sortedPlans.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center">
          <p className="text-sm text-slate-500">No subscription plans available.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sortedPlans.map((plan, idx) => {
            const isCurrent = currentPlan?.id === plan.id;
            const isPopular = idx === mostPopularIdx;
            const isAboveCurrent = plan.sort_order > currentSortOrder;
            const isBelowCurrent = currentPlan != null && plan.sort_order < currentSortOrder;
            const isFree = plan.price_monthly === 0;
            const isRedirecting = redirectingPlanId === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-shadow ${
                  isCurrent
                    ? 'border-brand-400 ring-2 ring-brand-400 shadow-md'
                    : isPopular
                    ? 'border-brand-100 shadow-lg scale-[1.02]'
                    : 'border-slate-100 hover:shadow-md'
                }`}
              >
                {/* Badges */}
                {(isPopular || isCurrent) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {isPopular && (
                      <span className="inline-block rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                        Most Popular
                      </span>
                    )}
                    {isCurrent && (
                      <span className="inline-block rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                        Current Plan
                      </span>
                    )}
                  </div>
                )}

                {/* Plan name */}
                <h3 className="mt-2 text-lg font-bold text-slate-900">{plan.name}</h3>

                {/* Price */}
                <div className="mt-3 mb-6">
                  {isFree ? (
                    <span className="text-3xl font-bold text-slate-900">Free</span>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-900">
                        ${plan.price_monthly}
                      </span>
                      <span className="text-sm text-slate-500">/mo</span>
                    </div>
                  )}
                  {!isFree && plan.price_annual > 0 && (
                    <p className="mt-1 text-xs text-slate-400">
                      ${plan.price_annual}/yr (save ${(plan.price_monthly * 12 - plan.price_annual).toFixed(0)})
                    </p>
                  )}
                </div>

                {/* Feature list */}
                <ul className="flex-1 space-y-3 text-sm">
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckIcon />
                    {formatLimit(plan.max_guards, 'guard', 'guards')}
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckIcon />
                    {formatLimit(plan.max_sites, 'site', 'sites')}
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckIcon />
                    {plan.max_uploads_per_month >= 9999
                      ? 'Unlimited uploads'
                      : `${plan.max_uploads_per_month} uploads/month`}
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    {plan.features?.analytics ? <CheckIcon /> : <XIcon />}
                    <span className={plan.features?.analytics ? '' : 'text-slate-400'}>
                      Analytics
                    </span>
                  </li>
                </ul>

                {/* Action button */}
                <div className="mt-6">
                  {isCurrent ? (
                    <button
                      disabled
                      className="w-full rounded-xl border-2 border-brand-100 bg-brand-50 px-4 py-2.5 text-sm font-semibold text-brand-600 cursor-not-allowed"
                    >
                      Current Plan
                    </button>
                  ) : isFree && isBelowCurrent ? (
                    <Link
                      to="/vendor/billing"
                      className="block w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-center text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Cancel to Free
                    </Link>
                  ) : isFree ? (
                    <span className="block w-full rounded-xl bg-slate-100 px-4 py-2.5 text-center text-sm font-semibold text-slate-500">
                      Free
                    </span>
                  ) : isAboveCurrent ? (
                    <button
                      onClick={() => handleChangePlan(plan)}
                      disabled={isRedirecting}
                      className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${
                        isPopular
                          ? 'bg-brand-600 hover:bg-brand-700'
                          : 'bg-slate-800 hover:bg-slate-900'
                      }`}
                    >
                      {isRedirecting ? (
                        <span className="inline-flex items-center gap-2">
                          <LoadingSpinner size="sm" className="text-white" />
                          Redirecting...
                        </span>
                      ) : (
                        'Upgrade'
                      )}
                    </button>
                  ) : isBelowCurrent ? (
                    <button
                      onClick={() => handleChangePlan(plan)}
                      disabled={isRedirecting || !plan.stripe_price_id_monthly}
                      className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-60"
                    >
                      {isRedirecting ? (
                        <span className="inline-flex items-center gap-2">
                          <LoadingSpinner size="sm" />
                          Redirecting...
                        </span>
                      ) : (
                        'Downgrade'
                      )}
                    </button>
                  ) : (
                    // Fallback: no current plan, non-free plan
                    <button
                      onClick={() => handleChangePlan(plan)}
                      disabled={isRedirecting}
                      className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 ${
                        isPopular
                          ? 'bg-brand-600 hover:bg-brand-700'
                          : 'bg-slate-800 hover:bg-slate-900'
                      }`}
                    >
                      {isRedirecting ? (
                        <span className="inline-flex items-center gap-2">
                          <LoadingSpinner size="sm" className="text-white" />
                          Redirecting...
                        </span>
                      ) : (
                        'Get Started'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
